import React from 'react';
import { Check } from 'lucide-react';
import { HelpHint } from '../../governed-mcp-gateway/components/GatewayUi';
import { API_TYPE_CATALOG, compatibilityLabel } from '../llmGatewayConstants';
import type { ApiCompatibility, GatewayApiType } from '../llmGatewayTypes';

function badgeClass(badge: ApiCompatibility['badge'], isLight: boolean): string {
  if (badge === 'recommended') return isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300';
  if (badge === 'compatible') return isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300';
  return isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-800 text-neutral-400';
}

export default function ApiTypeCards({
  compatibility,
  selected,
  onToggle,
  isLight,
}: {
  compatibility: ApiCompatibility[];
  selected: GatewayApiType[];
  onToggle: (apiType: GatewayApiType) => void;
  isLight: boolean;
}) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {API_TYPE_CATALOG.map((item) => {
        const match = compatibility.find((entry) => entry.apiType === item.id);
        const badge = match?.badge ?? 'unsupported';
        const disabled = badge === 'unsupported';
        const checked = selected.includes(item.id);
        return (
          <button
            key={item.id}
            type="button"
            disabled={disabled}
            onClick={() => onToggle(item.id)}
            className={`text-left rounded-2xl border p-4 transition-colors ${
              disabled
                ? isLight
                  ? 'border-neutral-200 bg-neutral-50 opacity-70 cursor-not-allowed'
                  : 'border-neutral-800 bg-neutral-950/40 opacity-60 cursor-not-allowed'
                : checked
                  ? isLight
                    ? 'border-indigo-300 bg-indigo-50/70 shadow-sm'
                    : 'border-indigo-500/40 bg-indigo-500/10'
                  : isLight
                    ? 'border-neutral-200 bg-white hover:border-neutral-300'
                    : 'border-neutral-800 bg-neutral-900 hover:border-neutral-700'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.label}</h3>
                  <HelpHint text={item.hint} />
                </div>
                <p className={`mt-1 font-mono text-[11px] ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.endpoint}</p>
              </div>
              <span className="flex items-center gap-2 shrink-0">
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${badgeClass(badge, isLight)}`}>
                  {compatibilityLabel(badge)}
                </span>
                <span
                  className={`h-5 w-5 rounded-md border flex items-center justify-center ${
                    checked
                      ? 'border-indigo-500 bg-indigo-600 text-white'
                      : isLight
                        ? 'border-neutral-300 bg-white'
                        : 'border-neutral-600 bg-neutral-900'
                  }`}
                >
                  {checked ? <Check size={12} strokeWidth={3} /> : null}
                </span>
              </span>
            </div>
            {disabled && match?.reason ? (
              <p className={`mt-2 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{match.reason}</p>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
